import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { BUTTON_FIELD_CLASSES } from "@shared/utils/constants";
import { AlertTriangle, Check, RefreshCw, User, X } from "lucide-react";
import {
  useRemoteUtilityId,
  useCurrency,
} from "@shared/selectors/globalSelectors";
import { toast } from "@shared/hooks/use-toast";
import { PaymentDetail } from "@features/cx/payments/types";
import { useTransferConsumerSearch, useCheckTransferPayment, useTransferPayment } from "../../../hooks";
import { Avatar, AvatarFallback } from "@shared/ui/avatar";
import { Input } from "@shared/ui/input";
import { Button } from "@shared/ui/button";
import { ValueLabel } from "@features/ax/shared/ValueLabel";

interface TransferPaymentDialogProps {
  isOpen: boolean;
  onClose: () => void;
  payment: any;
}

export const TransferPaymentDialog: React.FC<TransferPaymentDialogProps> = ({
  isOpen,
  onClose,
  payment,
}) => {
  const remoteUtilityId = useRemoteUtilityId();
  const currency = useCurrency();
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [selectedConsumer, setSelectedConsumer] = useState<any>(null);

  const { mutate: transferPaymentMutation, isPending: isTransferring } = useTransferPayment();

  const { data: checkData, isFetching: isChecking } = useCheckTransferPayment(
    payment?.id || payment?.paymentId,
    Number(remoteUtilityId),
    isOpen && !!payment,
  );

  // Debounce search term for user typing
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const { data: searchResults, isFetching } = useTransferConsumerSearch({
    search: debouncedSearchTerm,
    remoteUtilityId: Number(remoteUtilityId),
    excludeConsumerId: payment?.consumerId || 0,
  });

  const consumersList = searchResults?.slice(0, 6) || [];

  const fields: FormField[] = [
    {
      name: "consumerSelection",
      label: "Select New Consumer",
      type: "text",
      required: false,
      className: "col-span-1",
      customRender: () => (
        <div className="space-y-4">
          {!selectedConsumer ? (
            <div className="relative">
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name, account ID, or phone..."
                className="w-full pl-10"
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <User className="h-4 w-4 text-gray-400" />
              </div>

              {/* Search Results */}
              {debouncedSearchTerm.length >= 3 && (
                <div className="absolute z-10 w-full mt-1 bg-white rounded-md border border-gray-200 shadow-lg max-h-60 overflow-auto">
                  {isFetching ? (
                    <div className="p-4 text-center text-sm text-gray-500">
                      Searching...
                    </div>
                  ) : consumersList.length > 0 ? (
                    <ul className="divide-y divide-gray-100">
                      {consumersList.map((consumer: any) => (
                        <li
                          key={consumer.id}
                          className="p-3 hover:bg-gray-50 cursor-pointer flex items-center gap-3 transition-colors"
                          onClick={() => setSelectedConsumer(consumer)}
                        >
                          <Avatar className="h-10 w-10 border border-gray-200 shrink-0 bg-blue-50">
                            <AvatarFallback className="text-blue-600 text-xs font-semibold">
                              {getInitials(consumer.name)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-sm text-gray-900 truncate">
                                {consumer.name}
                              </span>
                              {consumer.statusDisplay?.toUpperCase() === "ACTIVE" && (
                                <span className="bg-green-100 text-green-700 px-1.5 py-0.5 rounded text-[10px] font-medium leading-none">
                                  Active
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-gray-500 mt-0.5 flex gap-2">
                              <span>Acc: {consumer.consumerNo}</span>
                              <span>•</span>
                              <span>
                                {consumer.contactNumber || "N/A"}
                              </span>
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="p-4 text-center text-sm text-gray-500">
                      No consumers found.
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="border border-blue-100 bg-blue-50/30 rounded-lg p-4 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Avatar className="h-12 w-12 border border-gray-200 bg-white">
                  <AvatarFallback className="text-blue-600 font-semibold">
                    {getInitials(selectedConsumer.name)}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h5 className="font-medium text-gray-900">
                      {selectedConsumer.name}
                    </h5>
                    {selectedConsumer.statusDisplay?.toUpperCase() === "ACTIVE" && (
                      <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded text-xs font-medium">
                        Active
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-gray-500 flex gap-3">
                    <span>Acc: {selectedConsumer.consumerNo}</span>
                    <span>
                      {selectedConsumer.contactNumber || "N/A"}
                    </span>
                  </div>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSelectedConsumer(null)}
              >
                Change
              </Button>
            </div>
          )}
        </div>
      ),
    },
    {
      name: "transferReason",
      label: "Transfer Reason",
      type: "textarea",
      placeholder: "Please provide a reason for transferring this payment...",
      className: "col-span-1 mt-2",
    },
  ];

  const form = FormService.createForm({
    fields: fields,
    defaultValues: {
      transferReason: "",
    },
  });

  const handleSubmit = (data: any) => {
    if (!selectedConsumer) {
      toast({
        variant: "destructive",
        description: "Select a consumer to transfer to.",
      });
      return;
    }

    const payload: any = {
      destination_consumer_id: selectedConsumer.id,
      remote_utility_id: Number(remoteUtilityId),
    };

    if (data.transferReason) {
      payload.transfer_reason = data.transferReason;
    }

    transferPaymentMutation(
      {
        paymentId: payment?.id || payment?.paymentId,
        payload,
      },
      {
        onSuccess: () => {
          handleClose();
        },
      }
    );
  };

  const handleClose = () => {
    form.reset();
    setSearchTerm("");
    setDebouncedSearchTerm("");
    setSelectedConsumer(null);
    onClose();
  };

  const getInitials = (name?: string) => {
    if (!name) return "";
    const parts = name.split(" ");
    return `${parts[0]?.charAt(0) || ""}${parts[1]?.charAt(0) || ""}`.toUpperCase();
  };

  if (!payment) return null;

  const paymentSummaryDetails = [
    {
      label: "Receipt ID",
      value: checkData?.receiptNo || payment.receiptNo || "N/A",
    },
    {
      label: "Amount",
      value: `${currency} ${checkData?.amount || payment.amount || "0.00"}`,
    },
    {
      label: "Payment Date",
      value: checkData?.paymentDate || payment.paymentDate || "N/A",
    },
    { label: "Payment Method", value: payment.paymentModeDisplay || "N/A" },
    {
      label: "Current Consumer",
      value: checkData
        ? `${checkData.currentConsumerName} (${checkData.currentConsumerNo})`
        : `${payment.consumerFirstName} ${payment.consumerLastName} (${payment.consumerNo || payment.consumerId})`,
      className: "col-span-2",
    },
  ];

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
    >
      <DialogContent className="sm:max-w-[700px] flex flex-col p-0 max-h-[90vh]">
        <DialogHeader className="px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <DialogTitle className="flex items-center gap-2 text-lg font-semibold text-gray-900">
            <RefreshCw className="w-5 h-5 text-gray-700" />
            Transfer Payment
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
          {/* Warning Notice */}
          {isChecking ? (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex gap-3 text-sm text-blue-800 items-start">
              <RefreshCw className="w-5 h-5 text-blue-500 shrink-0 mt-0.5 animate-spin" />
              <p>Checking payment transfer eligibility...</p>
            </div>
          ) : checkData?.isEligible === false ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex gap-3 text-sm text-red-800 items-start">
              <AlertTriangle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
              <p>{checkData.ineligibilityReason}</p>
            </div>
          ) : (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex gap-3 text-sm text-amber-800 items-start">
              <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
              <p>
                {checkData?.warning ||
                  "This payment entry will be marked as Reversed and a Credit Note will be generated for the selected consumer. This action cannot be easily undone."}
              </p>
            </div>
          )}

          {/* Payment Summary (Read-only) */}
          <div className="border border-gray-200 rounded-lg p-4 bg-gray-50/50">
            <h4 className="text-sm font-medium text-gray-900 mb-4">
              Payment Summary
            </h4>
            <div className="grid grid-cols-2 gap-y-4 gap-x-8 text-sm">
              {paymentSummaryDetails.map((item, index) => (
                <ValueLabel
                  key={index}
                  label={item.label}
                  value={isChecking ? "Loading..." : item.value}
                  className={item.className}
                />
              ))}
            </div>
          </div>

          {!isChecking && checkData?.isEligible !== false && (
            <div className="pt-2">
              <DynamicForm
                form={form}
                fields={fields}
                onSubmit={handleSubmit}
                gridColumns={1}
                responsiveColumns={{ sm: 1, md: 1, lg: 1 }}
                onReset={handleClose}
                loading={isTransferring}
                config={{
                  layout: "vertical",
                  showSubmitButton: true,
                  submitButtonText: "Confirm Transfer",
                  icon: Check,
                  showResetButton: true,
                  resetButtonText: "Cancel",
                  classes: BUTTON_FIELD_CLASSES,
                }}
              />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TransferPaymentDialog;
